/**
 * Settings → Recurring documents route.
 *
 * Management surface for recurring-document schedules — sales invoices and
 * vendor bills (ADR-0024 / ADR-0025). Lives under Settings ▸ Sales &
 * documents. The daily `recurring-schedules-tick` cron fires the schedules;
 * this page creates / pauses / cancels them and offers a manual "Generate
 * now". Replaced the standalone bill-templates feature.
 *
 * @see prototype/automations/recurring-schedules.html — visual reference
 */
import { createFileRoute, notFound } from "@tanstack/react-router";
import { hasPermission } from "@flint/api-contract";
import { meQueryOptions } from "../../../../lib/session.ts";
import { WorkspaceShell } from "../../../../features/workspace/index.ts";
import { SettingsShell } from "../../../../features/settings/index.ts";
import { RecurringSchedulesSection } from "../../../../features/settings/recurring/index.ts";

export const Route = createFileRoute("/(workspace)/settings/recurring/")({
  // URL-level `settings.manage` gate — see /settings/company/index.tsx for rationale.
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(meQueryOptions).catch(() => null);
    const perms = me?.company?.permissions ?? [];
    if (!hasPermission(perms, "settings.manage")) {
      throw notFound();
    }
  },
  component: RecurringSettingsPage,
});

function RecurringSettingsPage() {
  return (
    <WorkspaceShell breadcrumbs={[{ label: "Settings" }, { label: "Recurring documents" }]}>
      <SettingsShell
        activeSection="recurring"
        pageTitle="Recurring documents"
        pageDescription="Auto-draft a sales invoice or a vendor bill on a schedule — monthly retainers, quarterly fees, annual AMCs, recurring rent. Each run clones a source document; you review and issue/post the draft."
      >
        <RecurringSchedulesSection />
      </SettingsShell>
    </WorkspaceShell>
  );
}
